import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatDate, formatDateTime } from "../../hooks/dateTimeConvertion";
import { getLicenseType } from "../../hooks/useLicenseType";
import { timeRemainingTemplate } from "../../components/DatatableTemplates/LicenseTableTemplates";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { Container } from "../../components/Container/Container";
import CustomDropdown from "../../components/CustomInput/CustomDropdown";
import CustomInputNumberIncrement from "../../components/CustomInput/CustomInputNumberIncrement";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import { DataTable } from "primereact/datatable";
import { licenseMembers as licenseMembersColumns } from "../../components/DatatableTemplates/UserTableTemplates";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import LicenseMembersPickList from "../../components/LicenseMembersPickList/LicenseMembersPickList";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { BodyText, H2, DangerText, MediumText } from "../../styles/Typography";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import {
  IconUserFilled,
  IconCaseFilled,
  IconCompanyFilled,
  IconDocFilled,
  IconCalendarFilled,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconArrowLeft,
  IconCircleCheckFilled,
  IconUser,
  IconEdit,
  IconDelete,
  IconSearch,
  IconCircleCheck,
  IconCircleX,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  FlexColumn,
  ButtonContainer,
  ButtonLabel,
  DropdownColumn,
  SmallColumn,
  IsEnabled,
  Flex,
  InfoBlock,
  ButtonBlock,
  OuterHardwareContainer,
  SpaceContainer,
  HardwareInfoContainer,
  HardwareInfoContainerMobile,
  HardwareInfoInnerBox,
  HardwareInfoHeader,
  HardwareTextLine,
  HardwareInfoH2,
  HardwareInfoText,
  HardwareInfoH,
  PageName,
  SessionContainer,
  SessionContainerMobile,
  SessionCards,
  SessionCard,
  SessionNumber,
  OnlineNumber,
  NumberTag,
  OnlineNumberTag,
  ConfirmButtonContainer,
  SessionCardMobile,
  InputNumberContainer,
  InputNumberMobileFlex,
  SearchContainer,
  InputContainer,
} from "./LicenseDetails.styles";
import {
  FlexLineJustify,
  ButtonBox,
  Tag,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const LicenseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  const [isSessionLicense, setIsSessionLicense] = useState<boolean>(false);
  const tableRef = useRef<DataTable<any[]>>(null);
  const [tableData, setTableData] = useState<any[] | null>(null);
  const [selectedMember, setSelectedMember] = useState<any | null>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [editMaxMembers, setEditMaxMembers] = useState<boolean>(false);
  const [maxMembers, setMaxMembers] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [addUserOpt, setAddUserOpt] = useState<{ label: string; value: any }[]>(
    []
  );
  const [membersOpt, setMembersOpt] = useState<
    { id: string; fullName: string }[]
  >([]);
  const [editMembers, setEditMembers] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [reseting, setReseting] = useState<boolean>(false);
  const [hardwareDialogVisible, setHardwareDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");

  const {
    licensesEP,
    toastRef,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleClearHardware,
    handleEditMaxActiveUsers,
    handlePostSessionMember,
    handleDeleteSessionMember,
    refetchData,
  } = useLicensesAPI();
  const { usersEP, users } = useUsersAPI();
  const { companiesEP } = useCompaniesAPI();
  const { projectsEP } = useProjectsAPI();

  const {
    data: license,
    refetch: refetchLicense,
    isLoading,
  } = useGetQuery(`${licensesEP}/${id}`, {
    refetchOnMountOrArgChange: true,
    skip: !id || !licensesEP,
  });

  const { data: user, refetch: refetchUser } = useGetQuery(
    `${usersEP}/${license?.userId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !usersEP || !license || !license.userId,
    }
  );

  const { data: licenseMembers, refetch: refetchLicenseMembers } = useGetQuery(
    `${licensesEP}/${license?.id}/session/members`,
    {
      refetchOnMountOrArgChange: true,
      skip: !licensesEP || !license || !isSessionLicense,
    }
  );

  const { data: licenseSessions, refetch: refetchLicenseSessions } =
    useGetQuery(`${licensesEP}/${license?.id}/session`, {
      refetchOnMountOrArgChange: true,
      skip: !licensesEP || !license || !isSessionLicense,
    });

  const { data: company, refetch: refetchCompany } = useGetQuery(
    `${companiesEP}/${license?.organizationId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !companiesEP || !license,
    }
  );

  const { data: project, refetch: refetchProject } = useGetQuery(
    `${projectsEP}/${license?.projectId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !projectsEP || !license,
    }
  );

  useEffect(() => {
    if (license) {
      setComment(license.comments || "");
      setEnabled(license.isEnabled);
      setIsSessionLicense(license.type === "session");
    }
  }, [license]);

  useEffect(() => {
    if (licenseMembers) {
      const data = licenseMembers.map((member: any) => ({
        ...member,
        ...member.profile,
        isOnline: member.isOnline ?? false,
      }));

      setTableData(data);
    }
  }, [licenseMembers]);

  useEffect(() => {
    if (licenseSessions) setMaxMembers(licenseSessions.maxActiveSessions);
  }, [licenseSessions]);

  useEffect(() => {
    if (license?.organizationId && users) {
      const filtered = users
        .filter((user: any) => user.organizationId === license.organizationId)
        .map((user: any) => ({
          label: `${user.lastName ?? ""} ${user.name ?? ""} ${
            user.patronymic ?? ""
          }`,
          value: user.id,
        }));
      setAddUserOpt(filtered);
    } else {
      setAddUserOpt([]);
      setSelectedUser(null);
    }
  }, [license?.organizationId, users]);

  useEffect(() => {
    if (license?.organizationId && users) {
      const filtered = users
        .filter((user: any) => user.organizationId === license.organizationId)
        .map((user: any) => ({
          fullName: `${user.lastName ?? ""} ${user.name ?? ""} ${
            user.patronymic ?? ""
          }`,
          id: String(user.id),
        }));
      setMembersOpt(filtered);

      console.log("filtered", filtered);
    } else {
      setMembersOpt([]);
    }
  }, [license?.organizationId, users]);

  useEffect(() => {}, [license, refetchLicense]);

  const handleSave = async () => {
    if (!license) return;
    setSaving(true);
    let success = true;

    try {
      if (enabled !== license.isEnabled) {
        if (enabled) {
          success = await handleActivateLicense(license.id);
        } else {
          success = await handleDeactivateLicense(license.id);
        }
        if (!success) {
          setSaving(false);
          return;
        }
      }

      if (selectedUser) {
        success = await handleUpdateLicenseUser(license.id, selectedUser);

        if (!success) {
          setSaving(false);
          return;
        }
      }

      if (comment !== license.comments) {
        const editData = {
          startDate: license.startDate,
          expirationDate: license.expirationDate,
          comments: comment,
        };
        success = await handleUpdateLicense(editData, license.id);
        if (!success) {
          setSaving(false);
          return;
        }
      }
      refetchLicense();
    } finally {
      setSaving(false);
    }
  };

  const hadleClearHardwareInfo = async () => {
    setReseting(true);

    try {
      let success = await handleClearHardware(
        license.userId,
        license.projectId
      );
      if (!success) {
        setReseting(false);
        return;
      }
      refetchLicense();
    } finally {
      setHardwareDialogVisible(false);
    }
  };

  const handleEdit = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const handleDelete = () => {
    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicenseAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteLicense(license);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  const handleViewUser = (user: any) => {
    navigate(`/users/${user.userId}`);
  };

  const filteredData = useMemo(() => {
    if (!searchValue.trim() || !tableData) return tableData;
    const lowerSearch = searchValue.toLowerCase();
    return tableData.filter((user: any) => {
      const u = user as any;
      return (
        u.fullName?.toLowerCase().includes(lowerSearch) ||
        u.isOnline?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [tableData, searchValue]);

  const resetInputNumber = () => {
    setEditMaxMembers(false);
    setMaxMembers(licenseSessions.maxActiveSessions ?? 0);
  };

  const handleEditMaxMembers = async () => {
    if (!license || !maxMembers) return;

    try {
      let success = await handleEditMaxActiveUsers(license.id, maxMembers);
      if (!success) {
        setEditMaxMembers(false);
        return;
      }
      refetchLicense();
      refetchLicenseSessions();
    } finally {
      setEditMaxMembers(false);
    }
  };

  if (isLoading || !license)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Dialog
        visible={hardwareDialogVisible}
        onHide={() => {
          if (!hardwareDialogVisible) return;
          setHardwareDialogVisible(false);
        }}
        header="Информация об оборудовании"
        footer={
          <div>
            <Button
              label="Отмена"
              onClick={() => setHardwareDialogVisible(false)}
              outlined
            />
            <Button
              label="Сбросить"
              loading={reseting}
              onClick={hadleClearHardwareInfo}
            />
          </div>
        }
        style={{ width: "30rem" }}
      >
        <DangerText>Сбросить информацию об оборудовании?</DangerText>
      </Dialog>

      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные лицензии</H2>
              <ButtonBox>
                <Button text onClick={handleEdit}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={handleDelete}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>

            <FlexColumn>
              <StyledIconLine
                icon={<IconCaseFilled size={20} />}
                text={project?.name ?? "Нет данных"}
              />
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={`${company?.name ?? "Нет данных"}`}
              />
              {!isSessionLicense && (
                <StyledIconLine
                  icon={<IconUserFilled size={20} />}
                  text={
                    user
                      ? `${user?.lastName ?? ""} ${user?.name ?? ""} ${
                          user?.patronymic ?? ""
                        }`
                      : "Не добавлен"
                  }
                />
              )}
              <StyledIconLine
                icon={<IconDocFilled size={20} />}
                text={
                  license?.type
                    ? `${getLicenseType(license?.type)} лицензия`
                    : "Нет данных"
                }
              />
              <StyledIconLine
                icon={<IconCalendarFilled size={20} />}
                text={`Дата выдачи: ${
                  license?.issueDate
                    ? formatDate(license.issueDate.toString())
                    : "Нет данных"
                }`}
              />
              <StyledIconLine
                icon={<IconCalendarCheckFilled size={20} />}
                text={`Дата начала: ${
                  license?.startDate
                    ? formatDate(license.startDate.toString())
                    : "Нет данных"
                }`}
              />
              <StyledIconLine
                icon={<IconCalendarXFilled size={20} />}
                text={`Дата завершения: ${
                  license?.expirationDate
                    ? formatDate(license.expirationDate.toString())
                    : "Нет данных"
                }`}
              />
              <StyledIconLine
                icon={<IconCircleCheckFilled size={20} />}
                text={
                  <MediumText>
                    Оставшееся время:{" "}
                    {license ? timeRemainingTemplate(license) : "Нет данных"}
                  </MediumText>
                }
              />
              <StyledIconLine
                icon={<IconCalendarFilled size={20} />}
                text={`Последнее использование: ${
                  license?.lastUseDate
                    ? formatDateTime(license.lastUseDate.toString())
                    : "Не использовалась"
                }`}
              />
            </FlexColumn>
          </Container>
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </ContainerColumnFlex>

      <Flex>
        <InfoBlock>
          <H2>Управление лицензией</H2>

          <IsEnabled>
            <InputSwitch
              checked={enabled}
              onChange={(e: InputSwitchChangeEvent) => setEnabled(e.value)}
            />
            {enabled ? (
              <span>Лицензия активирована</span>
            ) : (
              <span>Лицензия деактивирована</span>
            )}
          </IsEnabled>

          {license?.userId || isSessionLicense ? (
            <></>
          ) : (
            <>
              {addUserOpt.length === 0 ? (
                <DangerText>В этой компании нет пользователей</DangerText>
              ) : (
                <DropdownColumn>
                  <BodyText>Добавить пользователя</BodyText>
                  <CustomDropdown
                    value={selectedUser}
                    onChange={(val) => setSelectedUser(val)}
                    options={addUserOpt}
                    placeholder="Пользователь"
                    icon={<IconUser size={20} />}
                    filter
                  />
                </DropdownColumn>
              )}
            </>
          )}

          <SmallColumn>
            <BodyText>Комментарии</BodyText>
            <InputTextarea
              autoResize
              value={comment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setComment(e.target.value)
              }
              rows={5}
              cols={30}
            />
          </SmallColumn>
          <ButtonBlock>
            <Button label="Сохранить" onClick={handleSave} loading={saving} />
          </ButtonBlock>
        </InfoBlock>

        {isSessionLicense ? (
          <SessionContainer>
            <FlexLineJustify>
              <PageName>
                <H2>Сессионные пользователи</H2>
                <Tag>
                  {isMobile ? "" : "Количество:"}{" "}
                  {licenseMembers?.length.toString() ?? "..."}
                </Tag>
              </PageName>
            </FlexLineJustify>

            <SessionCards>
              {editMaxMembers ? (
                <SessionCard>
                  <MediumText>
                    Максимальное количество активных пользователей:
                  </MediumText>
                  <InputNumberContainer>
                    <CustomInputNumberIncrement
                      value={maxMembers}
                      setValue={setMaxMembers}
                    />
                  </InputNumberContainer>
                  <ConfirmButtonContainer>
                    <Button
                      text
                      style={{ padding: "5px" }}
                      onClick={handleEditMaxMembers}
                    >
                      <IconCircleCheck size={20} />
                    </Button>
                    <Button
                      text
                      onClick={resetInputNumber}
                      style={{ padding: "5px" }}
                    >
                      <IconCircleX size={20} />
                    </Button>
                  </ConfirmButtonContainer>
                </SessionCard>
              ) : (
                <SessionCard>
                  <NumberTag>
                    <SessionNumber>
                      {licenseSessions
                        ? licenseSessions?.maxActiveSessions
                        : "..."}
                    </SessionNumber>
                  </NumberTag>
                  <MediumText>
                    Максимальное количество активных пользователей
                  </MediumText>
                  <div>
                    <Button text onClick={() => setEditMaxMembers(true)}>
                      <IconEdit size={16} />
                    </Button>
                  </div>
                </SessionCard>
              )}

              <SessionCard>
                <OnlineNumberTag>
                  <OnlineNumber>
                    {licenseSessions
                      ? licenseSessions?.activeSessionsCount
                      : "..."}
                  </OnlineNumber>
                </OnlineNumberTag>
                <MediumText>Активных пользователей сейчас</MediumText>
              </SessionCard>
            </SessionCards>

            {editMembers ? (
              <LicenseMembersPickList
                licenseId={license?.id || ""}
                currentMembers={licenseMembers || []}
                allUsers={membersOpt}
                maxMembers={licenseSessions?.maxActiveSessions || 0}
                handlePostSessionMember={handlePostSessionMember}
                handleDeleteSessionMember={handleDeleteSessionMember}
                onHide={() => setEditMembers(false)}
                refetchData={() => {
                  refetchLicenseMembers();
                  refetchLicenseSessions();
                }}
              />
            ) : (
              <>
                {" "}
                <SearchContainer>
                  <Button
                    outlined
                    tooltip="Редактировать список пользователей"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => setEditMembers(true)}
                  >
                    <IconEdit size={16} />
                  </Button>
                  <InputContainer>
                    <CustomInputText
                      value={searchValue}
                      setValue={setSearchValue}
                      placeholder="Поиск..."
                      icon={<IconSearch size={20} />}
                    />
                  </InputContainer>
                </SearchContainer>
                <CustomDatatable
                  ref={tableRef}
                  data={filteredData ?? []}
                  columns={licenseMembersColumns}
                  onView={handleViewUser}
                  onRowDoubleClick={handleViewUser}
                  getRowId={(user) => user.userId}
                  selected={selectedMember}
                  onSelectionChange={(user) => setSelectedMember(user)}
                  hideEditButton
                  hideDeleteButton
                  sortable
                />
              </>
            )}
          </SessionContainer>
        ) : (
          <OuterHardwareContainer>
            <SpaceContainer />
            <HardwareInfoContainer>
              <HardwareInfoHeader>
                <HardwareInfoH2>Hardware Info</HardwareInfoH2>
                {license?.hardwareInfo && (
                  <Button
                    severity="secondary"
                    tooltip="Сбросить информацию"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => setHardwareDialogVisible(true)}
                  >
                    <IconDelete size={20} />
                  </Button>
                )}
              </HardwareInfoHeader>

              <HardwareInfoInnerBox>
                <HardwareInfoText>
                  {license?.hardwareInfo
                    ? Object.entries(license.hardwareInfo).map(
                        ([key, value]) => (
                          <HardwareTextLine key={key}>
                            <HardwareInfoH>{key}:</HardwareInfoH>{" "}
                            {value != null ? String(value) : "—"}
                          </HardwareTextLine>
                        )
                      )
                    : "Нет данных"}
                </HardwareInfoText>
              </HardwareInfoInnerBox>
            </HardwareInfoContainer>
          </OuterHardwareContainer>
        )}
      </Flex>

      {isSessionLicense ? (
        <SessionContainerMobile>
          <FlexLineJustify>
            <PageName>
              <H2>Сессионные пользователи</H2>
              <Tag>
                {isMobile ? "" : "Количество:"}{" "}
                {licenseMembers?.length.toString() ?? "..."}
              </Tag>
            </PageName>
          </FlexLineJustify>

          <SessionCards>
            {editMaxMembers ? (
              <SessionCardMobile>
                <MediumText>
                  Максимальное количество активных пользователей:
                </MediumText>
                <InputNumberMobileFlex>
                  <InputNumberContainer>
                    <CustomInputNumberIncrement
                      value={maxMembers}
                      setValue={setMaxMembers}
                    />
                  </InputNumberContainer>
                  <ConfirmButtonContainer>
                    <Button
                      text
                      style={{ padding: "5px" }}
                      onClick={handleEditMaxMembers}
                    >
                      <IconCircleCheck size={20} />
                    </Button>
                    <Button
                      text
                      onClick={resetInputNumber}
                      style={{ padding: "5px" }}
                    >
                      <IconCircleX size={20} />
                    </Button>
                  </ConfirmButtonContainer>
                </InputNumberMobileFlex>
              </SessionCardMobile>
            ) : (
              <SessionCard>
                <NumberTag>
                  <SessionNumber>
                    {licenseSessions
                      ? licenseSessions?.maxActiveSessions
                      : "..."}
                  </SessionNumber>
                </NumberTag>
                <MediumText>
                  Максимальное количество активных пользователей
                </MediumText>
                <div>
                  <Button text onClick={() => setEditMaxMembers(true)}>
                    <IconEdit size={16} />
                  </Button>
                </div>
              </SessionCard>
            )}

            <SessionCard>
              <OnlineNumberTag>
                <OnlineNumber>
                  {licenseSessions
                    ? licenseSessions?.activeSessionsCount
                    : "..."}
                </OnlineNumber>
              </OnlineNumberTag>
              <MediumText>Активных пользователей сейчас</MediumText>
            </SessionCard>
          </SessionCards>

          {editMembers ? (
            <LicenseMembersPickList
              licenseId={license?.id || ""}
              currentMembers={licenseMembers || []}
              allUsers={membersOpt}
              maxMembers={licenseSessions?.maxActiveSessions || 0}
              handlePostSessionMember={handlePostSessionMember}
              handleDeleteSessionMember={handleDeleteSessionMember}
              onHide={() => setEditMembers(false)}
              refetchData={() => {
                refetchLicenseMembers();
                refetchLicenseSessions();
              }}
            />
          ) : (
            <>
              <SearchContainer>
                <Button
                  outlined
                  tooltip="Редактировать список пользователей"
                  tooltipOptions={{ position: "top" }}
                  onClick={() => setEditMembers(true)}
                >
                  <IconEdit size={16} />
                </Button>
                <InputContainer>
                  <CustomInputText
                    value={searchValue}
                    setValue={setSearchValue}
                    placeholder="Поиск..."
                    icon={<IconSearch size={20} />}
                  />
                </InputContainer>
              </SearchContainer>
              <CustomDatatable
                ref={tableRef}
                data={filteredData ?? []}
                columns={licenseMembersColumns}
                onView={handleViewUser}
                onRowDoubleClick={handleViewUser}
                getRowId={(user) => user.userId}
                selected={selectedMember}
                onSelectionChange={(user) => setSelectedMember(user)}
                hideEditButton
                hideDeleteButton
                sortable
              />
            </>
          )}
        </SessionContainerMobile>
      ) : (
        <HardwareInfoContainerMobile>
          <HardwareInfoHeader>
            <HardwareInfoH2>Hardware Info</HardwareInfoH2>
            {license?.hardwareInfo && (
              <Button
                severity="secondary"
                tooltip="Сбросить информацию"
                tooltipOptions={{ position: "top" }}
                onClick={() => setHardwareDialogVisible(true)}
              >
                <IconDelete size={20} />
              </Button>
            )}
          </HardwareInfoHeader>

          <HardwareInfoInnerBox>
            <HardwareInfoText>
              {license?.hardwareInfo
                ? Object.entries(license.hardwareInfo).map(([key, value]) => (
                    <HardwareTextLine key={key}>
                      <HardwareInfoH>{key}:</HardwareInfoH>{" "}
                      {value != null ? String(value) : "—"}
                    </HardwareTextLine>
                  ))
                : "Нет данных"}
            </HardwareInfoText>
          </HardwareInfoInnerBox>
        </HardwareInfoContainerMobile>
      )}

      <LicenseDialogs
        visible={licenseDialogVisible}
        onHide={() => setLicenseDialogVisible(false)}
        dialogType={licenseDialogType}
        license={license}
        project={project}
        company={company}
        usersList={users}
        enrichedLicense={{
          ...license,
          licenseType: getLicenseType(license.type),
        }}
        handleEditLicense={handleUpdateLicense}
        handleEditLicenseUser={handleUpdateLicenseUser}
        handleDeleteLicense={deleteLicenseAPI}
        refetchData={refetchLicense}
        licensesEP={licensesEP}
      />
    </ContainerFlex>
  );
};

export default LicenseDetails;
